import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { KAL_DIALOG_DATA, KalDate, KalDialogRef } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { GetListeExerciceBaseModelAction } from 'src/app/_shared/stores/exercices/exercices.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { getISODate, getKalDateFromObject } from 'src/app/utils/date.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import {
    ArrayOfFactureDto,
    ExerciceModelDto,
    FactureAvoirModelDtoInput,
    FactureDto,
    FactureHistoriqueModel,
    JournalModelDto,
    LigneAvoirModelDtoInput,
    ListeJournauxAndExerciceModelDto,
    MutationGetListeFacturesGeneriqueArgs,
    MutationGetListeJournauxAndExerciceModelArgs,
    MutationSaveFactureAvoirArgs,
    StatusDtoEnum
} from 'src/graphql/generated-types';
import { environment } from 'src/environments/environment';
import { constants } from 'src/environments/constants';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { toNumber } from 'src/app/utils/number.utils';

const MAX_PERCENTAGE = 100;

export interface SharedGenerateCreditDialogData {

    accepted?: boolean;

    invoice?: FactureHistoriqueModel;

    amount?: number;

    credit?: FactureDto;

    activityId?: number;
}

@Component({
    selector: 'app-shared-generate-credit-dialog',
    templateUrl: './shared-generate-credit-dialog.component.html',
    styleUrls: ['./shared-generate-credit-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedGenerateCreditDialogComponent extends BaseComponent implements OnInit {

    form: FormGroup;

    journalsList: JournalModelDto[] = [];

    exercicesList: ExerciceModelDto[] = [];

    invoiceInformations: FactureDto = null;

    constructor(private readonly fb: FormBuilder,
                private readonly graphqlService: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                private readonly dialogRef: KalDialogRef<SharedGenerateCreditDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedGenerateCreditDialogData) {
        super(cdr);
    }

    get invoice(): FactureHistoriqueModel {
        return this.data.invoice;
    }

    get creditLabel(): string {
        const creditsList = get(this.invoice, ['ListeAvoirsLies', 'string'], []);

        if (arrayEmpty(creditsList)) {
            return '';
        }

        return creditsList.length === 1
            ? `L'avoir n°${creditsList[0]} a déjà été généré à partir de cette facture.`
            : `Les avoirs n°${creditsList.join(', ')} ont déjà été générés à partir de cette facture.`;
    }

    get type(): string {
        return this.form.get('Type').value;
    }

    get canImpute(): boolean {
        return this.invoice.StatutFacture !== constants.facture.statut.SOLDE;
    }

    submit(): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'SaveFactureAvoir', MutationSaveFactureAvoirArgs>({
            mutation: gql`
                mutation SaveFactureAvoir(
                    $contexte: ContextInfosInput,
                    $factureModel: FactureAvoirModelDTOInput
                ) {
                    SaveFactureAvoir(
                        contexte: $contexte
                        factureModel: $factureModel
                    ) {
                        Id
                        Solde
                        NumFacture
                        Benef {
                            Id
                            Nom
                            Prenom
                            CodeMembre
                        }
                        ListeAcompteAvoirImpute {
                            FactureImputationAcompteAvoirDTO {
                                MontantImputation
                            }
                        }
                    }
                }
            `,
            variables: {
                factureModel: this.getCredit()
            }
        }).pipe(
            tap(SaveFactureAvoir => {
                this.data.credit = SaveFactureAvoir;
                this.data.accepted = true;
                this.dialogRef.close(this.data);
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    close(): void {
        this.dialogRef.close();
    }

    private getInvoicesInformations(): Observable<ArrayOfFactureDto> {
        this.loading = true;
        return this.graphqlService.mutateOne<'GetListeFacturesGenerique', MutationGetListeFacturesGeneriqueArgs>({
            mutation: gql`
                mutation GetListeFacturesGenerique(
                    $contexte: ContextInfosInput,
                    $criteres: CriteresFactureDTOInput
                ) {
                    GetListeFacturesGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        FactureDTO {
                            Id
                            NumFacture
                            MontantFactureTotal
                            IsLibre
                            TypeFacture
                            Activite {
                                CompteCollectifClient {
                                    Id
                                    Libelle
                                    Code
                                    Numero
                                }
                            }
                            Fournisseur {
                                Id
                            }
                            Benef {
                                Id
                                Nom
                                Prenom
                                CodeMembre
                            }
                            Produit {
                                Id
                            }
                            StatutFacture {
                                Code
                            }
                            CompteEcriture {
                                Journal {
                                    Id
                                }
                                NumeroPiece
                                ListeMouvementComptable {
                                    MouvementComptableDTO {
                                        Id
                                        Libelle
                                        Montant
                                        Compte {
                                            Id
                                            Libelle
                                            Code
                                            Numero
                                        }
                                        CompteTiers {
                                            Id
                                        }
                                    }
                                }
                            }
                            ListeLigneFacture {
                                LigneFactureDTO {
                                    Article {
                                        Id
                                    }
                                    MontantUnitaire
                                    MontantSubAccepteUnitaire
                                    RemiseMontantUnitaire
                                    Quantite
                                    CompteVente {
                                        Id
                                        Libelle
                                        Code
                                        Numero
                                    }
                                    CompteCollectif {
                                        Id
                                        Libelle
                                        Code
                                        Numero
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    Id: this.invoice.IdFacture,
                    TakeLigneFacture: true,
                    TakeCompteAchatVente: true,
                    TakeCompteCollectif: true,
                    TakeBenefCompteTiers: true,
                    TakeMouvementComptable: true,
                    TakeCompteEcriture: true,
                    TakeLigneCommandeSurLigneFacture: true,
                    TakeArticleLigneFacture: true,
                    TakeProduit: true,
                    TakeFournisseur: true,
                    TakeStatut: true,
                    TakeFactureAvoirForEcritureComptable: true
                }
            }
        }).pipe(
            tap(GetListeFacturesGenerique => {
                this.invoiceInformations = GetListeFacturesGenerique?.FactureDTO?.[0] || null;
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private getCredit(): FactureAvoirModelDtoInput {
        const {IdExercice, IdJournal, DateFact, Amount, Comment, IsImputeAvoirSurFact, NumeroAvoir} = this.form.value;
        let ListeLigneAvoirModel: LigneAvoirModelDtoInput[] = [];

        // Calcul le montant avoirisé en fonction du type sélectionné : Montant ou Pourcentage
        const Montant = this.type === 'amount' ? Amount : +(this.invoiceInformations.MontantFactureTotal * Amount / MAX_PERCENTAGE).toFixed(2);
        const invoice: FactureAvoirModelDtoInput = {
            IsAvoir: true,
            IsGenereeCompta: true,
            CodeTypeEcriture: this.invoiceInformations?.Fournisseur ? 'AF' : 'AC',
            IdFactureConcerneAvoir: this.invoiceInformations.Id,
            IdFacture: this.invoiceInformations.Id,
            NumFacture: NumeroAvoir || null,
            IdActivite: this.data.activityId,
            TypeFac: this.invoiceInformations.TypeFacture,
            Objet: `Avoir sur la facture n°${this.invoiceInformations.NumFacture}`,
            IdBenef: this.invoiceInformations.Benef?.Id,
            IdFournisseur: this.invoiceInformations.Fournisseur?.Id,
            IdProduit: get(this.invoiceInformations, ['Produit', 'Id'], null),
            IsLibre: this.invoiceInformations.IsLibre,
            MontantFactureTotal: this.invoiceInformations.MontantFactureTotal,
            IdPointVente: this.snapshot(UserState.userContext).CurrentPV,
            IdDelegation: this.snapshot(UserState.currentDelegation).Id,
            MontantAImputerSurAvoirPartiel: +Montant,
            DateFact: getISODate(DateFact),
            IdExercice,
            IdJournal,
            IsImputeAvoirSurFact,
            NumeroPieceAuto: true,
            RemarquePrive: get(Comment, 'privateComment', null),
            RemarquePublique: get(Comment, 'publicComment', null)
        };

        if (get(this.invoiceInformations, ['CompteEcriture', 'ListeMouvementComptable', 'MouvementComptableDTO'], false)) {
            const CompteCollectifClient = this.invoiceInformations.Activite.CompteCollectifClient;
            ListeLigneAvoirModel =
                this.invoiceInformations.CompteEcriture.ListeMouvementComptable.MouvementComptableDTO
                    .filter(e => !e.CompteTiers)
                    .map(e => {
                        return {
                            IdMouvement: e.Id,
                            LibelleLigneAvoir: e.Libelle,
                            Status: StatusDtoEnum.Inserted,
                            Montant: e.Montant,
                            Quantite: 1,
                            CompteModelAchatVente: {
                                IdCompte: e.Compte.Id,
                                LibelleCompte: e.Compte.Libelle,
                                Code: e.Compte.Code,
                                Numero: e.Compte.Numero
                            },
                            CompteModelCollectif: {
                                IdCompte: CompteCollectifClient.Id,
                                LibelleCompte: CompteCollectifClient.Libelle,
                                Code: CompteCollectifClient.Code,
                                Numero: CompteCollectifClient.Numero
                            }
                        };
                    }) as LigneAvoirModelDtoInput[];
        } else {
            ListeLigneAvoirModel = this.invoiceInformations.ListeLigneFacture.LigneFactureDTO
                .map(e => {
                    return {
                        IdArticle: get(e, ['Article', 'Id'], null),
                        LibelleLigneAvoir: `Avoir sur la facture N°${this.invoiceInformations.NumFacture}`,
                        Status: StatusDtoEnum.Inserted,
                        Montant: e.MontantUnitaire,
                        MontantSubventionUnitaire: e.MontantSubAccepteUnitaire,
                        MontantRemise: e.RemiseMontantUnitaire,
                        Quantite: e.Quantite,
                        CompteModelAchatVente: {
                            IdCompte: e.CompteVente.Id,
                            LibelleCompte: e.CompteVente.Libelle,
                            Code: e.CompteVente.Code,
                            Numero: e.CompteVente.Numero
                        },
                        CompteModelCollectif: {
                            IdCompte: e.CompteCollectif.Id,
                            LibelleCompte: e.CompteCollectif.Libelle,
                            Code: e.CompteCollectif.Code,
                            Numero: e.CompteCollectif.Numero
                        }
                    };
                }) as LigneAvoirModelDtoInput[];
        }

        invoice.ListeLigneAvoirModel = {
            LigneAvoirModelDTO: ListeLigneAvoirModel
        };

        return invoice;
    }

    private getJournal(): Observable<ListeJournauxAndExerciceModelDto> {
        const isPurchase = !!this.invoice.IdFournisseur;

        return this.graphqlService.mutateOne<'GetListeJournauxAndExerciceModel', MutationGetListeJournauxAndExerciceModelArgs>({
            mutation: gql`
            mutation GetListeJournauxAndExerciceModel(
                $contexte: ContextInfosInput,
                $critere: CriteresModelJournauxExerciceDTOInput
            ) {
                GetListeJournauxAndExerciceModel(
                    contexte: $contexte
                    critere: $critere
                ) {
                    ListeJournauxModel {
                        JournalModelDTO {
                            IdJournal
                            LibelleJournal
                        }
                    }
                    JournalVenteActModel {
                        IdJournal
                    }
                }
            }
        `,
            variables: {
                critere: {
                    WithJournaux: true,
                    WithActivite: true,
                    CritereJourn: {
                        IsVente: !isPurchase ? true : null,
                        IsAchat: isPurchase ? true : null
                    },
                    CritereAct: {
                        TakeJournalVente: !isPurchase,
                        TakeJournalAchat: isPurchase
                    }
                }
            }
        }).pipe(
            tap(GetListeJournauxAndExerciceModel => {
                this.journalsList = GetListeJournauxAndExerciceModel.ListeJournauxModel.JournalModelDTO;
                const idJournal = isPurchase ? GetListeJournauxAndExerciceModel.JournalAchatActModel?.IdJournal : GetListeJournauxAndExerciceModel.JournalVenteActModel?.IdJournal;
                const selectedJournal = this.journalsList.find(e => e.IdJournal === idJournal);
                if (selectedJournal) {
                    this.form.get('IdJournal').patchValue(selectedJournal.IdJournal);
                }
            })
        );

    }

    private getExercice$(): Observable<any> {
        return this.dispatch$(new GetListeExerciceBaseModelAction()).pipe(
            tap(({exerciceState}) => {
                const DateDebutFacture = getKalDateFromObject(this.invoice, 'DateFacture');
                const getDate: (exercice) => Record<string, KalDate> = (exercice) => ({
                    DateDebutExercice: getKalDateFromObject(exercice, 'DateDebut'),
                    DateFinExercice: getKalDateFromObject(exercice, 'DateFin'),
                });

                this.exercicesList = cloneDeep(exerciceState?.exercicesBaseModel || [])
                    .sort((a, b) => ('' + a.CodeExercice > '' + b.CodeExercice) ? -1 : 1);

                let selectedExercice: ExerciceModelDto = this.exercicesList.find(e => {
                    return DateDebutFacture.isBetween(getDate(e).DateDebutExercice, getDate(e).DateFinExercice);
                });

                if (!selectedExercice) {
                    selectedExercice = this.exercicesList.find(e => {
                        return new KalDate().isBetween(getDate(e).DateDebutExercice, getDate(e).DateFinExercice);
                    });
                }

                if (selectedExercice) {
                    this.form.get('IdExercice').patchValue(selectedExercice.IdExercice);
                }
            })
        );
    }

    private setValidators(): void {
        const amountControl = this.form.get('Amount');
        const isAmount = this.form.get('Type').value === 'amount';

        amountControl.setValidators([
            Validators.required,
            FormValidator.number,
            FormValidator.greaterThan(0),
            Validators.max(isAmount ? this.data.amount : MAX_PERCENTAGE),
        ]);

        amountControl.updateValueAndValidity({emitEvent: false});
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            DateFact: [new KalDate(), [
                FormValidator.date,
                FormValidator.minDateValidator(getKalDateFromObject(this.invoice, 'DateFacture')),
                FormValidator.maxDateValidator(new KalDate())
            ]],
            Type: ['amount'],
            IdJournal: [null, Validators.required],
            IdExercice: [null, Validators.required],
            Amount: [this.data.amount || 0],
            Comment: [null, Validators.maxLength(environment.constants.maxLengthComment)],
            PartNumber: [null],
            IsImputeAvoirSurFact: [this.canImpute],
            NumeroAvoir: [`A${this.invoice.NumFacture}`, Validators.maxLength(15)]
        });

        this.setValidators();

        this.watch$ = this.form.get('Type').valueChanges.pipe(
            tap(value => {
                const amount = value === 'amount' ? this.data.amount || 0 : MAX_PERCENTAGE;
                this.form.get('Amount').reset(amount, {emitEvent: false});
                this.setValidators();
            })
        );

        this.watch$ = forkJoin([
            this.getJournal(),
            this.getExercice$(),
            this.getInvoicesInformations()
        ]).pipe(
            tap(() => {
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

}
